import React, { useEffect, useRef, useState } from 'react';
import { fetchBanners, saveBanner, removeBanner } from '../services/careerBannerApi';

const bannerSize = { label: 'Career banner', width: 1440, height: 435 };
const legacyVariants = ['desktop', 'laptop', 'tablet', 'mobile'];

function BannerSlot({ current, onSaved }) {
  const size = bannerSize;
  const input = useRef(null);
  const selection = useRef(0);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [dimensions, setDimensions] = useState(null);
  const [alt, setAlt] = useState(current?.alt || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    if (!file) { setPreview(''); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  useEffect(() => () => { selection.current += 1; }, []);

  async function choose(event) {
    const selected = event.target.files?.[0];
    event.target.value = '';
    if (!selected) return;
    const ticket = ++selection.current;
    setError(''); setMessage('');
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(selected.type) || selected.size > 5 * 1024 * 1024) {
      setError('Choose a JPG, PNG or WEBP image up to 5 MB.'); return;
    }
    const url = URL.createObjectURL(selected);
    try {
      const image = new Image();
      image.src = url;
      await image.decode();
      if (ticket !== selection.current) return;
      setFile(selected); setDimensions({ width: image.naturalWidth, height: image.naturalHeight }); setConfirmRemove(false);
    } catch (_) { if (ticket === selection.current) setError('This image could not be opened. Choose another image.'); }
    finally { URL.revokeObjectURL(url); }
  }
  async function save(event) {
    event.preventDefault();
    if (!file) return;
    setBusy(true); setError(''); setMessage('');
    try { const result = await saveBanner(file, alt); onSaved(result); setFile(null); setDimensions(null); setMessage('Banner saved. It is now used on the careers page.'); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }
  async function remove() {
    setBusy(true); setError(''); setMessage('');
    try { await removeBanner(); onSaved(null); setFile(null); setAlt(''); setConfirmRemove(false); setMessage('Image removed. The fallback banner will be used.'); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }
  const source = (file ? preview : '') || current?.url;
  return <form className="panel banner-slot" aria-label="Career banner settings" onSubmit={save}>
    <h2>{size.label}</h2>
    <p className="attachment-help">Recommended size: <strong>{size.width} × {size.height} px</strong></p>
    <div className="banner-preview" style={{ aspectRatio: `${size.width} / ${size.height}` }}>
      {source ? <img src={source} alt={file ? alt : current?.alt || 'Banner preview'} /> : <span>Default careers banner will be used</span>}
    </div>
    {file && <p className="attachment-help"><strong>{file.name}</strong> · {dimensions?.width} × {dimensions?.height} px</p>}
    {file && dimensions && (dimensions.width !== size.width || dimensions.height !== size.height) && <p className="banner-size-hint">For best results, use {size.width} × {size.height} px. This image will be fitted and may be cropped.</p>}
    <label className="banner-alt">Image description (optional)<input maxLength={250} value={alt} disabled={busy || !file} onChange={event => setAlt(event.target.value)} placeholder="Describe the banner image" /></label>
    <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="attachment-file-input" aria-label="Choose career banner" onChange={choose} disabled={busy} />
    <div className="banner-actions">
      <button type="button" className="secondary-action" disabled={busy} onClick={() => input.current?.click()}>{file || current ? 'Replace image' : 'Choose image'}</button>
      {file && <><button type="button" className="secondary-action" disabled={busy} onClick={() => { selection.current += 1; setFile(null); setDimensions(null); setAlt(current?.alt || ''); setError(''); }}>Remove selection</button><button className="primary-action" disabled={busy}>{busy ? 'Saving...' : 'Save banner'}</button></>}
      {current && !file && <button type="button" className="attachment-remove" disabled={busy} onClick={() => setConfirmRemove(true)}>Remove saved image</button>}
    </div>
    {confirmRemove && <div className="banner-remove-confirm"><p>Remove this saved image and use the fallback banner?</p><button type="button" className="attachment-remove" disabled={busy} onClick={remove}>Remove image</button> <button type="button" className="secondary-action" disabled={busy} onClick={() => setConfirmRemove(false)}>Cancel</button></div>}
    {error && <p className="attachment-error" role="alert">{error}</p>}
    {message && <p className="banner-success" role="status">{message}</p>}
  </form>;
}

export default function CareerBannerSettings() {
  const [banners, setBanners] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    fetchBanners().then(data => { if (active) setBanners(data); }).catch(err => { if (active) setError(err.message); });
    return () => { active = false; };
  }, [attempt]);
  const currentBanner = legacyVariants.map(variant => banners?.[variant]).find(image => image?.url);
  return <>
    <div className="page-heading"><div><span>WEBSITE CONTENT</span><h1>Careers Banner</h1><p>Upload one banner for the Current Openings page. JPG, PNG or WEBP, up to 5 MB per image.</p></div></div>
    <p className="banner-settings-help">One image is used on all screen sizes and adjusts automatically. Recommended size: 1440 ? 435 px. Keep the title and breadcrumb area clear.</p>
    {error && <div role="alert" className="attachment-error">{error} <button type="button" className="secondary-action" onClick={() => { setError(''); setAttempt(value => value + 1); }}>Retry</button></div>}
    {!banners && !error && <p>Loading banners...</p>}
    {banners && <div className="banner-single-setting"><BannerSlot current={currentBanner} onSaved={item => setBanners(item ? { desktop: item } : {})} /></div>}
  </>;
}
